// Where a visitor came from, kept in this browser until they sign in, then sent to the server once
// (POST /api/profile/attribution). Two touches: the first known marketing contact (never replaced)
// and the latest one. A touch is a visit with UTM tags, or one that came from someone else's site;
// a direct visit is not a touch. Only the referrer's host and the landing page's path are kept.

export type Touch = {
  source: string
  medium: string
  campaign: string
  content: string
  term: string
  referrer: string
  landing: string
  at: string
}

const FIRST_KEY = 'lupa_attr_first'
const LAST_KEY = 'lupa_attr_last'
const SENT_KEY = 'lupa_attr_sent'

// Hops inside sign-in and payment flows are not marketing: our own sites, Keycloak, the social
// sign-in providers and the payment page.
const NOT_MARKETING_HOSTS = [
  'oauth.yandex.ru', 'passport.yandex.ru', 'id.vk.com', 'oauth.vk.com', 'id.vk.ru',
  'yookassa.ru', 'yoomoney.ru',
]

function siteOf(host: string) {
  return host.toLowerCase().replace(/^www\./, '').split('.').slice(-2).join('.')
}

function hostOf(url: string) {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    return ''
  }
}

function isMarketingReferrer(refHost: string, ownHost: string, extraOwnHosts: string[]) {
  if (!refHost) return false
  const own = [ownHost, ...extraOwnHosts].filter(Boolean).map(siteOf)
  if (own.includes(siteOf(refHost))) return false
  return !NOT_MARKETING_HOSTS.some((h) => refHost === h || refHost.endsWith(`.${h}`))
}

/** The touch this page view represents, or null for a direct visit / an auth or payment hop. */
export function touchFromVisit(href: string, referrer: string, extraOwnHosts: string[] = [], now = new Date()): Touch | null {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  const p = url.searchParams
  const tag = (name: string) => (p.get(name) || '').trim().slice(0, 200)
  const utm = {
    source: tag('utm_source'),
    medium: tag('utm_medium'),
    campaign: tag('utm_campaign'),
    content: tag('utm_content'),
    term: tag('utm_term'),
  }
  const refHost = hostOf(referrer)
  const external = isMarketingReferrer(refHost, url.hostname, extraOwnHosts)
  const hasUtm = Object.values(utm).some(Boolean)
  // The return from Keycloak carries code/state: the referrer is the sign-in page, not a campaign.
  const authCallback = p.has('code') && p.has('state')
  if (!hasUtm && (!external || authCallback)) return null
  return {
    ...utm,
    source: utm.source || refHost,
    medium: utm.medium || (hasUtm ? '' : 'referral'),
    referrer: external ? refHost : '',
    landing: url.pathname,
    at: now.toISOString(),
  }
}

function read(key: string): Touch | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as Touch) : null
  } catch {
    return null
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Private mode or blocked storage: attribution is a nice-to-have.
  }
}

function keycloakHost() {
  return hostOf(String(import.meta.env.VITE_KEYCLOAK_URL || ''))
}

/** Remembers this page view if it is a marketing touch. Call once per page load. */
export function captureAttribution(href = window.location.href, referrer = document.referrer) {
  const touch = touchFromVisit(href, referrer, [keycloakHost()])
  if (!touch) return
  if (!read(FIRST_KEY)) write(FIRST_KEY, JSON.stringify(touch))
  write(LAST_KEY, JSON.stringify(touch))
}

/**
 * Sends the remembered touches for this account unless this very last touch was already sent for
 * it. A failure (no consent yet, offline) leaves them for the next sign-in or page load.
 */
export async function syncAttribution(userKey: string, send: (first: Touch | null, last: Touch | null) => Promise<unknown>) {
  const first = read(FIRST_KEY)
  const last = read(LAST_KEY)
  if (!userKey || (!first && !last)) return
  const marker = `${userKey}|${last?.at || first?.at || ''}`
  try {
    if (localStorage.getItem(SENT_KEY) === marker) return
  } catch {
    // Unreadable storage: sending again is harmless — the server keeps the first touch anyway.
  }
  try {
    await send(first, last)
    write(SENT_KEY, marker)
  } catch {
    // Try again next time.
  }
}
